import { BASIS_LABELS, setBasis, useBasis } from "@app/lib/basis";
import type { Basis } from "@app/lib/basis";
import { useDateRange } from "@app/lib/date-range";
import { useRouter } from "@tanstack/react-router";

const OPTIONS: Basis[] = ["CASH", "NORMALIZED"];

// The global cost-recognition basis, applied across the cost screens. Writes
// localStorage and invalidates the router so route loaders refetch on the new
// basis — the same full-suspend transition as the date-range switcher. The
// always-visible selected pill segment is the primary mode indicator.
export default function BasisSwitcher() {
  const router = useRouter();
  const current = useBasis();

  const select = (value: Basis) => {
    if (value === current) return;
    setBasis(value);
    router.invalidate();
  };

  return (
    <div
      className="flex items-center rounded border border-gray-300 overflow-hidden text-xs"
      title="Cost-recognition basis — applied to the cost screens"
    >
      {OPTIONS.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => select(option)}
          className={`px-2 py-1 ${
            option === current
              ? "bg-gray-900 text-white"
              : "bg-white text-gray-600 hover:bg-gray-50"
          }`}
        >
          {BASIS_LABELS[option]}
        </button>
      ))}
    </div>
  );
}

// The per-chart reinforcement badge: shown only under the normalized basis, it
// reminds the reader the costs are smoothed (the cash default needs no badge).
export function BasisBadge() {
  const basis = useBasis();
  if (basis !== "NORMALIZED") return null;

  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-gray-300 text-gray-600">
      Normalized
    </span>
  );
}

// The below-chart divergence note: under the normalized basis, a bounded date
// range can truncate a redistribution that spilled outside the window, so the
// windowed total need not tie out to cash. Shown whenever basis=normalized AND a
// window is set — a deliberately conservative annotation (no per-stakeholder
// detection), so it can over-fire on a window with no actual redistribution.
export function BasisDivergenceNote() {
  const basis = useBasis();
  const range = useDateRange();
  if (basis !== "NORMALIZED" || range === "ALL") return null;

  return (
    <p className="text-xs text-gray-400">
      Normalized costs are smoothed across the months they cover. Inside a bounded
      date range, a redistribution that spills outside the window is truncated, so
      the windowed total may not match the cash total. Switch to "All time" to
      reconcile.
    </p>
  );
}
